import React from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Plus, Trash2, Stethoscope } from "lucide-react";

// Friendly editor for a paediatric drug's per-route dosing. Renders one editable
// block per route of administration so clinicians never touch raw JSON. Returns
// the full updated document via the controlled `value` / `onChange` API; any
// other fields on the document are preserved untouched.

const ROUTE_OPTIONS = ["PO", "IV", "IM", "SC", "PR", "IN", "Buccal", "Nebulised", "MDI", "NG"];

const numOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

function RouteDoseRow({ rd, index, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...rd, [k]: v });
  return (
    <div className="p-3 border border-slate-200 rounded-md bg-white space-y-2" data-testid={`paedform-route-${index}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1">
          <Label className="text-xs">Route of administration *</Label>
          <Input
            value={rd.route || ""}
            onChange={(e) => set("route", e.target.value)}
            placeholder="e.g. PO, IV, IM, PR"
            className="mt-1 text-sm font-semibold w-40"
            list="paed-route-options"
            data-testid={`paedform-route-name-${index}`}
          />
        </div>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={onRemove}
          className="text-rose-600 hover:bg-rose-50 mt-5"
          title="Remove this route"
          data-testid={`paedform-route-remove-${index}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div>
          <Label className="text-xs">Dose / kg</Label>
          <Input
            type="number"
            step="any"
            value={rd.dosePerKg ?? ""}
            onChange={(e) => set("dosePerKg", numOrNull(e.target.value) ?? 0)}
            placeholder="e.g. 15"
            className="mt-1 text-sm"
            data-testid={`paedform-dose-${index}`}
          />
          <div className="text-[10px] text-slate-400 mt-0.5">0 = not weight-based</div>
        </div>
        <div>
          <Label className="text-xs">Upper (range)</Label>
          <Input
            type="number"
            step="any"
            value={rd.dosePerKgHigh ?? ""}
            onChange={(e) => set("dosePerKgHigh", numOrNull(e.target.value))}
            placeholder="optional"
            className="mt-1 text-sm"
            data-testid={`paedform-dose-high-${index}`}
          />
        </div>
        <div>
          <Label className="text-xs">Unit</Label>
          <Input
            value={rd.doseUnit || ""}
            onChange={(e) => set("doseUnit", e.target.value)}
            placeholder="mg / mcg / mL"
            className="mt-1 text-sm"
            data-testid={`paedform-unit-${index}`}
          />
        </div>
        <div>
          <Label className="text-xs">Frequency</Label>
          <Input
            value={rd.frequency || ""}
            onChange={(e) => set("frequency", e.target.value)}
            placeholder="e.g. 6-hourly"
            className="mt-1 text-sm"
            data-testid={`paedform-freq-${index}`}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Max single dose</Label>
          <Input
            type="number"
            step="any"
            value={rd.maxSingleDose ?? ""}
            onChange={(e) => set("maxSingleDose", numOrNull(e.target.value))}
            placeholder="optional cap"
            className="mt-1 text-sm"
            data-testid={`paedform-max-${index}`}
          />
        </div>
        <div>
          <Label className="text-xs">Max unit</Label>
          <Input
            value={rd.maxSingleUnit || ""}
            onChange={(e) => set("maxSingleUnit", e.target.value)}
            placeholder="mg / mcg"
            className="mt-1 text-sm"
            data-testid={`paedform-max-unit-${index}`}
          />
        </div>
      </div>

      <div>
        <Label className="text-xs">Route-specific notes</Label>
        <Textarea
          value={rd.notes || ""}
          onChange={(e) => set("notes", e.target.value)}
          rows={2}
          placeholder="e.g. Infuse over 15 min; dilution / safety caution for this route…"
          className="mt-1 text-sm"
          data-testid={`paedform-notes-${index}`}
        />
      </div>
    </div>
  );
}

export default function PaedRouteDoseForm({ value, onChange }) {
  const doc = value || {};
  const set = (k, v) => onChange({ ...doc, [k]: v });
  const routeDoses = Array.isArray(doc.routeDoses) ? doc.routeDoses : [];

  const setRoute = (i, next) => {
    const arr = routeDoses.slice();
    arr[i] = next;
    set("routeDoses", arr);
  };
  const removeRoute = (i) => set("routeDoses", routeDoses.filter((_, idx) => idx !== i));
  const addRoute = () =>
    set("routeDoses", [
      ...routeDoses,
      { route: "", dosePerKg: 0, dosePerKgHigh: null, doseUnit: doc.doseUnit || "mg", frequency: "", maxSingleDose: null, maxSingleUnit: null, notes: "" },
    ]);

  return (
    <div className="space-y-4" data-testid="paed-route-dose-form">
      <datalist id="paed-route-options">
        {ROUTE_OPTIONS.map((r) => <option key={r} value={r} />)}
      </datalist>

      {/* Drug-level context */}
      <div className="p-3 border border-slate-200 rounded-md bg-slate-50/60 space-y-3">
        <div className="flex items-center gap-2">
          <Stethoscope className="w-4 h-4 text-[#111827]" />
          <span className="font-heading font-semibold text-sm text-[#111827]">Drug details</span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={doc.name || ""} onChange={(e) => set("name", e.target.value)} className="mt-1 text-sm" data-testid="paedform-name" />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Input value={doc.category || ""} onChange={(e) => set("category", e.target.value)} className="mt-1 text-sm" data-testid="paedform-category" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Indication</Label>
          <Input value={doc.indication || ""} onChange={(e) => set("indication", e.target.value)} className="mt-1 text-sm" data-testid="paedform-indication" />
        </div>
        <div>
          <Label className="text-xs">Routes summary (badge text)</Label>
          <Input value={doc.route || ""} onChange={(e) => set("route", e.target.value)} placeholder="e.g. PO / IV / PR" className="mt-1 text-sm" data-testid="paedform-route-summary" />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">General notes</Label>
            <Textarea value={doc.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1 text-sm" data-testid="paedform-general-notes" />
          </div>
          <div>
            <Label className="text-xs">STG reference</Label>
            <Input value={doc.stgRef || ""} onChange={(e) => set("stgRef", e.target.value)} className="mt-1 text-sm" data-testid="paedform-stgref" />
          </div>
        </div>
      </div>

      {/* Per-route doses */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="font-heading font-semibold text-sm text-[#111827]">Dosing per route of administration</span>
          <span className="text-xs text-slate-500">{routeDoses.length} route{routeDoses.length === 1 ? "" : "s"}</span>
        </div>
        {routeDoses.length === 0 && (
          <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-md p-4 text-center mb-2">
            No route-specific doses yet. Add one per route (PO, IV, IM…).
          </div>
        )}
        <div className="space-y-3">
          {routeDoses.map((rd, i) => (
            <RouteDoseRow key={i} rd={rd} index={i} onChange={(next) => setRoute(i, next)} onRemove={() => removeRoute(i)} />
          ))}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={addRoute} className="mt-2 text-[#111827] border-[#0B5FFF]/30" data-testid="paedform-add-route">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add route
        </Button>
      </div>

      <p className="text-[11px] text-slate-500 px-1">
        Saving here updates the dosing live (once approved) and locks the drug from the periodic sync. Other fields are preserved untouched.
      </p>
    </div>
  );
}
